import {
  DocumentPublishedFormat, FileDocument, FileDocumentStatus, Time, UiConfig, UiMenuItem, fileDocumentName, formatPublished,
} from '@/model';
import api from '@/rest-api';
import Vue from 'vue';

type IncludedDocumentStatus = FileDocumentStatus | number | undefined;

function resolveDocumentStatus(status: IncludedDocumentStatus) {
  if (status === undefined) return undefined;
  if (typeof status === 'number') {
    return FileDocumentStatus.options.find((option) => option.ordinal === status);
  }
  return FileDocumentStatus.options.find((option) => option.value === status);
}

function createStore() {
  const state = Vue.observable({
    config: null as UiConfig | null,
  });

  const dispatch = {
    async load(): Promise<void> {
      if (state.config !== null) return;
      try {
        state.config = await api.uiConfig();
      } catch (e) {
        console.warn('Failed to load ui-config, using built-in defaults.', e);
        state.config = {};
      }
    },
  };

  return { state, dispatch };
}

const uiConfig = createStore();
export default uiConfig;

function hiddenDocumentStatuses(): readonly FileDocumentStatus[] {
  return uiConfig.state.config?.documents?.hiddenStatuses ?? [];
}

export const defaultMenuItems: readonly UiMenuItem[] = [
  { title: 'Home', icon: 'mdi-home-outline', route: '/' },
  {
    title: 'Planning',
    icon: 'mdi-tag-multiple',
    children: [
      { title: 'Taxonomy - Manager', icon: 'mdi-tag', route: '/label' },
      { title: 'Taxonomy Category', icon: 'mdi-tag-group', route: '/label-type' },
    ],
  },
  {
    title: 'Collection',
    icon: 'mdi-file-cabinet',
    children: [
      { title: 'Documents', icon: 'mdi-file-document-outline', route: '/document' },
      { title: 'Publishers', icon: 'mdi-domain', route: '/publisher' },
    ],
  },
  {
    title: 'Analysis',
    icon: 'mdi-file-chart',
    children: [
      { title: 'Selection', icon: 'mdi-file-document-outline', route: '/selection' },
      { title: 'Summaries', icon: 'mdi-air-filter', route: '/summary' },
      { title: 'Assistant', icon: 'mdi-robot-outline', route: '/assistant' },
    ],
  },
  {
    title: 'Dissemination',
    icon: 'mdi-bullhorn',
    children: [],
  },
];

function findTitleInItems(items: readonly UiMenuItem[], route: string): string | undefined {
  for (const item of items) {
    if (item.route === route) return item.title;
    if (item.children) {
      const found = findTitleInItems(item.children, route);
      if (found) return found;
    }
  }
  return undefined;
}

export function titleForRoute(route: string): string | undefined {
  const items = uiConfig.state.config?.menu?.items ?? defaultMenuItems;
  return findTitleInItems(items, route);
}

export function isDocumentStatusVisible(status: FileDocumentStatus): boolean {
  return !hiddenDocumentStatuses().includes(status);
}

export function documentStatusOptions(included: readonly IncludedDocumentStatus[] = []) {
  const hidden = new Set(hiddenDocumentStatuses());
  const includedStatuses = new Set(included
    .map((status) => resolveDocumentStatus(status)?.value)
    .filter((status): status is FileDocumentStatus => status !== undefined));

  return FileDocumentStatus.options.filter((option) => includedStatuses.has(option.value) || !hidden.has(option.value));
}

export function summariesEnabled(): boolean {
  return uiConfig.state.config?.features?.summaries ?? false;
}

export function documentPublishedFormat(): DocumentPublishedFormat {
  return uiConfig.state.config?.documents?.publishedFormat ?? 'year';
}

export function documentPublishedLabel(): string {
  return documentPublishedFormat() === 'date' ? 'Publish date' : 'Publish year';
}

export function formatDocumentPublished(time: Time | undefined): string | undefined {
  return formatPublished(time, documentPublishedFormat());
}

export function fileDocumentDisplayName(document: Partial<FileDocument>): string {
  return fileDocumentName(document, documentPublishedFormat());
}

export function documentLabelTypes(): readonly string[] {
  return uiConfig.state.config?.documents?.documentLabelTypes ?? [];
}

export function defaultPersonalView(): boolean {
  return uiConfig.state.config?.documents?.defaultPersonalView ?? false;
}

export function documentResultPinnedTypes(): readonly string[] {
  return uiConfig.state.config?.labelPicker?.documentResultPinnedTypes ?? [];
}

// When a label of `type` is picked in the advanced label picker, `add` codes are
// force-added to the fragment and `suggest` codes are surfaced as 'related'
// suggestions for the labeller to accept. Codes are resolved to labels by the
// caller to avoid a dependency on the labels store here.
export function autoAddSpecForType(type: string): { add: readonly string[]; suggest: readonly string[] } {
  const spec = uiConfig.state.config?.labelPicker?.autoAddOnType?.[type];
  return { add: spec?.add ?? [], suggest: spec?.suggest ?? [] };
}

// Returns null (unrestricted — caller should show everything) when neither
// key is configured for this deployment. Once mlSuggestionPools is set,
// themes not listed in it simply contribute no extra types.
export function mlSuggestionTypesForThemes(themeCodes: readonly string[]): readonly string[] | null {
  const labelPicker = uiConfig.state.config?.labelPicker;
  if (labelPicker?.mlSuggestionPools === undefined && labelPicker?.mlAlwaysVisibleTypes === undefined) {
    return null;
  }
  const pools = labelPicker?.mlSuggestionPools ?? {};
  const always = labelPicker?.mlAlwaysVisibleTypes ?? [];
  const fromThemes = themeCodes.flatMap((code) => pools[code] ?? []);
  return [...new Set([...always, ...fromThemes])];
}
